import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';
import { calculateYearTotals } from '../../../utils/calculateYearTotals';
import { format, parseISO } from 'date-fns';

const { FiDollarSign, FiSave, FiEdit, FiPlus, FiTrash2, FiX, FiCalendar, FiTrendingUp, FiBarChart, FiInfo, FiRefreshCw, FiCheck, FiAlertTriangle, FiCopy } = FiIcons;

const RevenueGMManagement = () => {
  const { data, updateData, dataYear, dataAvailableYears, setDataYear, initializeRevenueGmYear } = useAuth0Data();
  const [isEditingGoals, setIsEditingGoals] = useState(false);
  const [showDataPointForm, setShowDataPointForm] = useState(false);
  const [editingDataPoint, setEditingDataPoint] = useState(null);
  const [goalFormData, setGoalFormData] = useState({ revenueGoal: 0, grossMarginGoal: 0 });
  const [dataPointForm, setDataPointForm] = useState({
    date: '',
    revenue: 0,
    grossMargin: 0,
    description: '',
    type: 'monthly'
  });
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...dataAvailableYears, currentYear, currentYear + 1])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    setDataYear(parseInt(e.target.value));
  };

  const handleInitializeYear = async () => {
    const previousYear = dataYear - 1;
    if (window.confirm(`Initialize ${dataYear} by copying goals from ${previousYear}?\n\nThis will copy the revenue and gross margin goals.`)) {
      setIsInitializing(true);
      try {
        await initializeRevenueGmYear(dataYear);
      } catch (error) {
        alert(error.message);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  // Update form data when data changes
  useEffect(() => {
    console.log('Data updated in RevenueGMManagement:', data.revenue, data.grossMargin);
    setGoalFormData({
      revenueGoal: data.revenue.goal || 0,
      grossMarginGoal: data.grossMargin.goal || 0
    });
    setHistoricalData(data.historicalRevenueGM || []);
  }, [data.revenue.goal, data.grossMargin.goal, data.historicalRevenueGM]);

  // Calculate selected year values using shared utility
  const calculateCurrentYearValues = () => {
    const { totalRevenue, totalGrossMargin } = calculateYearTotals(historicalData, dataYear);
    return { currentRevenue: totalRevenue, currentGrossMargin: totalGrossMargin };
  };

  // Get latest data point for date reference (filtered by selected year)
  const getLatestDataPoint = () => {
    if (historicalData.length === 0) return null;
    const yearData = historicalData.filter(p => p.date && new Date(p.date).getFullYear() === dataYear);
    if (yearData.length === 0) return null;
    const sortedData = [...yearData].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sortedData[0];
  };

  const calculateYearProgress = (dataPointDate) => {
    if (!dataPointDate) return 0;
    const date = new Date(dataPointDate);
    const year = date.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);
    const daysPassed = Math.floor((date - startOfYear) / (1000 * 60 * 60 * 24));
    const totalDaysInYear = Math.floor((endOfYear - startOfYear) / (1000 * 60 * 60 * 24)) + 1;
    return (daysPassed / totalDaysInYear) * 100;
  };

  const calculateExpectedProgress = (goal, yearProgress) => {
    return (goal * yearProgress) / 100;
  };

  const latestDataPoint = getLatestDataPoint();
  const yearProgress = latestDataPoint ? calculateYearProgress(latestDataPoint.date) : 0;

  // Current values using corrected calculation
  const { currentRevenue, currentGrossMargin } = calculateCurrentYearValues();

  // Expected progress based on time elapsed
  const expectedRevenue = calculateExpectedProgress(data.revenue.goal, yearProgress);
  const expectedGrossMargin = calculateExpectedProgress(data.grossMargin.goal, yearProgress);

  // Progress percentages
  const revenueProgress = data.revenue.goal > 0 ? (currentRevenue / data.revenue.goal) * 100 : 0;
  const grossMarginProgress = data.grossMargin.goal > 0 ? (currentGrossMargin / data.grossMargin.goal) * 100 : 0;

  // Performance vs expected
  const revenueVsExpected = expectedRevenue > 0 ? ((currentRevenue - expectedRevenue) / expectedRevenue) * 100 : 0;
  const gmVsExpected = expectedGrossMargin > 0 ? ((currentGrossMargin - expectedGrossMargin) / expectedGrossMargin) * 100 : 0;

  const handleSaveGoals = async () => {
    setLoading(true);
    setSaveStatus('🔄 Saving goals...');
    try {
      console.log('🔄 Saving goals:', goalFormData);

      // Update revenue goals
      const updatedRevenue = {
        current: currentRevenue, // Use calculated current year value
        goal: parseInt(goalFormData.revenueGoal) || 0,
        ytd: data.revenue.ytd || 0,
        pace: data.revenue.pace || 0
      };

      // Update gross margin goals
      const updatedGrossMargin = {
        current: currentGrossMargin, // Use calculated current year value
        goal: parseInt(goalFormData.grossMarginGoal) || 0,
        ytd: data.grossMargin.ytd || 0,
        pace: data.grossMargin.pace || 0
      };

      console.log('🔄 Updating revenue data:', updatedRevenue);
      console.log('🔄 Updating gross margin data:', updatedGrossMargin);

      // Save both updates
      await updateData('revenue', updatedRevenue);
      await updateData('grossMargin', updatedGrossMargin);

      console.log('✅ Goals saved successfully');
      setIsEditingGoals(false);
      setSaveStatus('✅ Goals saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('❌ Error updating goals:', error);
      setSaveStatus(`❌ Failed to save goals: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGoals = () => {
    // Reset form to current values
    setGoalFormData({
      revenueGoal: data.revenue.goal || 0,
      grossMarginGoal: data.grossMargin.goal || 0
    });
    setIsEditingGoals(false);
    setSaveStatus('');
  };

  const handleAddDataPoint = async () => {
    console.log('🔍 handleAddDataPoint called with form data:', JSON.stringify(dataPointForm, null, 2));
    console.log('🔍 dataPointForm.date:', dataPointForm.date);
    console.log('🔍 dataPointForm.date type:', typeof dataPointForm.date);
    console.log('🔍 dataPointForm.date falsy?', !dataPointForm.date);

    if (!dataPointForm.date || (dataPointForm.revenue === 0 && dataPointForm.grossMargin === 0)) {
      setSaveStatus('❌ Please enter a date and at least one value (revenue or gross margin)');
      setTimeout(() => setSaveStatus(''), 5000);
      return;
    }

    // Check for yearly data point conflicts in the same year
    const newDataYear = new Date(dataPointForm.date).getFullYear();
    const existingYearlyInSameYear = historicalData.find(point => 
      point.type === 'yearly' && 
      new Date(point.date).getFullYear() === newDataYear
    );

    if (dataPointForm.type === 'yearly' && existingYearlyInSameYear) {
      setSaveStatus(`❌ A yearly data point already exists for ${newDataYear}. Please edit the existing one or use a different year.`);
      setTimeout(() => setSaveStatus(''), 8000);
      return;
    }

    setLoading(true);
    setSaveStatus('🔄 Adding data point...');
    try {
      const newDataPoint = {
        id: Date.now(),
        ...dataPointForm,
        revenue: parseInt(dataPointForm.revenue) || 0,
        grossMargin: parseInt(dataPointForm.grossMargin) || 0,
        createdAt: new Date().toISOString()
      };

      const updatedHistoricalData = [...historicalData, newDataPoint].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      console.log('🔄 Updating historical data with new point:', newDataPoint);

      // Update historical data in database
      await updateData('historicalRevenueGM', updatedHistoricalData);

      // Update local state
      setHistoricalData(updatedHistoricalData);
      setDataPointForm({
        date: '',
        revenue: 0,
        grossMargin: 0,
        description: '',
        type: 'monthly'
      });
      setShowDataPointForm(false);
      setSaveStatus('✅ Data point added successfully!');
      setTimeout(() => setSaveStatus(''), 3000);

      console.log('✅ Data point added successfully');
    } catch (error) {
      console.error('❌ Error adding data point:', error);
      setSaveStatus(`❌ Failed to add data point: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDataPoint = (dataPoint) => {
    setEditingDataPoint(dataPoint.id);

    // Convert date to YYYY-MM-DD format for HTML date input
    let formattedDate = '';
    if (dataPoint.date) {
      try {
        const dateObj = typeof dataPoint.date === 'string' ? parseISO(dataPoint.date) : new Date(dataPoint.date);
        formattedDate = format(dateObj, 'yyyy-MM-dd');
      } catch (error) {
        console.error('Error formatting date for editing:', error);
        formattedDate = '';
      }
    }

    setDataPointForm({
      date: formattedDate,
      revenue: dataPoint.revenue,
      grossMargin: dataPoint.grossMargin,
      description: dataPoint.description,
      type: dataPoint.type
    });
  };

  const handleUpdateDataPoint = async () => {
    // Validate that date is not empty
    if (!dataPointForm.date) {
      setSaveStatus('❌ Please enter a date for the data point');
      setTimeout(() => setSaveStatus(''), 5000);
      return;
    }

    // Check for yearly data point conflicts in the same year (excluding current point)
    const newDataYear = new Date(dataPointForm.date).getFullYear();
    const existingYearlyInSameYear = historicalData.find(point =>
      point.id !== editingDataPoint &&
      point.type === 'yearly' &&
      new Date(point.date).getFullYear() === newDataYear
    );

    if (dataPointForm.type === 'yearly' && existingYearlyInSameYear) {
      setSaveStatus(`❌ Another yearly data point already exists for ${newDataYear}. Please use a different year or edit that point instead.`);
      setTimeout(() => setSaveStatus(''), 8000);
      return;
    }

    setLoading(true);
    setSaveStatus('🔄 Updating data point...');
    try {
      const updatedHistoricalData = historicalData.map(point =>
        point.id === editingDataPoint
          ? {
              ...point,
              ...dataPointForm,
              revenue: parseInt(dataPointForm.revenue) || 0,
              grossMargin: parseInt(dataPointForm.grossMargin) || 0,
              updatedAt: new Date().toISOString()
            }
          : point
      ).sort((a, b) => new Date(a.date) - new Date(b.date));

      console.log('🔄 Updating historical data:', updatedHistoricalData);

      // Update historical data in database
      await updateData('historicalRevenueGM', updatedHistoricalData);

      // Update local state
      setHistoricalData(updatedHistoricalData);
      setEditingDataPoint(null);
      setDataPointForm({
        date: '',
        revenue: 0,
        grossMargin: 0,
        description: '',
        type: 'monthly'
      });
      setSaveStatus('✅ Data point updated successfully!');
      setTimeout(() => setSaveStatus(''), 3000);

      console.log('✅ Data point updated successfully');
    } catch (error) {
      console.error('❌ Error updating data point:', error);
      setSaveStatus(`❌ Failed to update data point: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDataPoint = async (id) => {
    if (!confirm('Are you sure you want to delete this data point?')) return;

    setLoading(true);
    setSaveStatus('🔄 Deleting data point...');
    try {
      const updatedHistoricalData = historicalData.filter(point => point.id !== id);

      console.log('🔄 Deleting data point:', id);

      // Update historical data in database
      await updateData('historicalRevenueGM', updatedHistoricalData);

      // Update local state
      setHistoricalData(updatedHistoricalData);
      setSaveStatus('✅ Data point deleted successfully!');
      setTimeout(() => setSaveStatus(''), 3000);

      console.log('✅ Data point deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting data point:', error);
      setSaveStatus(`❌ Failed to delete data point: ${error.message}`);
      setTimeout(() => setSaveStatus(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDataPointEdit = () => {
    setEditingDataPoint(null);
    setShowDataPointForm(false);
    setDataPointForm({
      date: '',
      revenue: 0,
      grossMargin: 0,
      description: '',
      type: 'monthly'
    });
    setSaveStatus('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percent) => {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(1)}%`;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'monthly': return 'bg-blue-100 text-blue-800';
      case 'quarterly': return 'bg-purple-100 text-purple-800';
      case 'yearly': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'monthly': return FiCalendar;
      case 'quarterly': return FiBarChart;
      case 'yearly': return FiTrendingUp;
      default: return FiCalendar;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-gray-900">Revenue & Gross Margin</h3>
                <select
                  value={dataYear}
                  onChange={handleYearChange}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 font-medium bg-white"
                >
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <p className="text-sm text-gray-500">Goals for {dataYear} - Shared across all users</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Save Status */}
            {saveStatus && (
              <div className={`flex items-center px-3 py-1 rounded-lg text-sm font-medium ${
                saveStatus.includes('✅') || saveStatus.includes('success') ? 'bg-green-100 text-green-800' :
                saveStatus.includes('❌') || saveStatus.includes('Failed') ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {loading && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                {saveStatus.includes('✅') && <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />}
                {saveStatus.includes('❌') && <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mr-2" />}
                <span className="whitespace-pre-wrap">{saveStatus}</span>
              </div>
            )}

            <button
              onClick={() => setShowDataPointForm(true)}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
              Add Data Point
            </button>

            {isEditingGoals ? (
              <>
                <button
                  onClick={handleSaveGoals}
                  disabled={loading}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={loading ? FiRefreshCw : FiSave} className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Saving...' : 'Save Goals'}
                </button>
                <button
                  onClick={handleCancelGoals}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditingGoals(true)}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiEdit} className="w-4 h-4 mr-2" />
                Edit Goals
              </button>
            )}
          </div>
        </div>

        {/* Progress Information */}
        {latestDataPoint && latestDataPoint.date && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 mb-2">{dataYear} Calculation</h4>
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Yearly data points:</strong> Use latest value for YTD (not cumulative)
                </p>
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Monthly data points:</strong> Sum all values (cumulative)
                </p>
                <p className="text-sm text-blue-800">
                  Latest data point from: <strong>{format(typeof latestDataPoint.date === 'string' ? parseISO(latestDataPoint.date) : new Date(latestDataPoint.date), 'MMMM dd, yyyy')}</strong>
                  <br />
                  Year progress: <strong>{yearProgress.toFixed(1)}%</strong> of the year elapsed
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Summary Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">{dataYear} Revenue</p>
            <p className="text-xl font-bold text-blue-600">{formatCurrency(currentRevenue)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {revenueProgress.toFixed(1)}% of goal
            </p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600">{dataYear} GM</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(currentGrossMargin)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {grossMarginProgress.toFixed(1)}% of goal
            </p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600">Revenue vs Expected</p>
            <p className={`text-xl font-bold ${revenueVsExpected >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercentage(revenueVsExpected)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Expected: {formatCurrency(expectedRevenue)}
            </p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600">GM vs Expected</p>
            <p className={`text-xl font-bold ${gmVsExpected >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatPercentage(gmVsExpected)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Expected: {formatCurrency(expectedGrossMargin)}
            </p>
          </div>
        </div>

        {/* Annual Goals Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Revenue Goal */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">Annual Revenue Goal</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Revenue Target
              </label>
              {isEditingGoals ? (
                <input
                  type="number"
                  value={goalFormData.revenueGoal}
                  onChange={(e) => setGoalFormData(prev => ({ ...prev, revenueGoal: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter revenue goal"
                  disabled={loading}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.revenue.goal)}</p>
                  {data.revenue.goal === 0 && (
                    <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">
                      No goal set
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Revenue Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress to Goal</span>
                <span>{revenueProgress.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(revenueProgress, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Current: {formatCurrency(currentRevenue)}</span>
                <span>Goal: {formatCurrency(data.revenue.goal)}</span>
              </div>
            </div>
          </div>

          {/* Gross Margin Goal */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">Annual Gross Margin Goal</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Gross Margin Target
              </label>
              {isEditingGoals ? (
                <input
                  type="number"
                  value={goalFormData.grossMarginGoal}
                  onChange={(e) => setGoalFormData(prev => ({ ...prev, grossMarginGoal: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter gross margin goal"
                  disabled={loading}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(data.grossMargin.goal)}</p>
                  {data.grossMargin.goal === 0 && (
                    <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">
                      No goal set
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* GM Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress to Goal</span>
                <span>{grossMarginProgress.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(grossMarginProgress, 100)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Current: {formatCurrency(currentGrossMargin)}</span>
                <span>Goal: {formatCurrency(data.grossMargin.goal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Point Form */}
      {(showDataPointForm || editingDataPoint) && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            {editingDataPoint ? 'Edit' : 'Create'} Revenue & GM Data Point
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
                Date *
              </label>
              <input
                type="date"
                value={dataPointForm.date}
                onChange={(e) => setDataPointForm(prev => ({ ...prev, date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={dataPointForm.type}
                onChange={(e) => setDataPointForm(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              >
                <option value="monthly">Monthly (adds to total)</option>
                <option value="quarterly">Quarterly (adds to total)</option>
                <option value="yearly">Yearly (YTD snapshot - replaces previous yearly)</option>
              </select>
              {dataPointForm.type === 'yearly' && (
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ Yearly data points represent YTD values, not additions. Only one per year allowed.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Revenue ($)</label>
              <input
                type="number"
                value={dataPointForm.revenue}
                onChange={(e) => setDataPointForm(prev => ({ ...prev, revenue: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gross Margin ($)</label>
              <input
                type="number"
                value={dataPointForm.grossMargin}
                onChange={(e) => setDataPointForm(prev => ({ ...prev, grossMargin: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="0"
                disabled={loading}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
              <input
                type="text"
                value={dataPointForm.description}
                onChange={(e) => setDataPointForm(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., End of Q2 2024, July Month End"
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <button
              onClick={editingDataPoint ? handleUpdateDataPoint : handleAddDataPoint}
              disabled={loading || !dataPointForm.date || (dataPointForm.revenue === 0 && dataPointForm.grossMargin === 0)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={loading ? FiRefreshCw : FiSave} className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Saving...' : (editingDataPoint ? 'Update' : 'Create')} Data Point
            </button>
            <button
              onClick={handleCancelDataPointEdit}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Historical Data Points */}
      <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-6">Historical Data Points</h4>
        
        {historicalData.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiBarChart} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No historical data points yet.</p>
            <p className="text-sm">Create your first data point to start tracking performance over time.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {historicalData
                .filter(point => point.date) // Filter out entries with null dates
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((dataPoint, index) => (
                  <motion.div
                    key={dataPoint.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.05 }}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <SafeIcon icon={getTypeIcon(dataPoint.type)} className="w-4 h-4 text-gray-600" />
                            <span className="font-medium text-gray-900">
                              {dataPoint.date && format(typeof dataPoint.date === 'string' ? parseISO(dataPoint.date) : new Date(dataPoint.date), 'MMMM dd, yyyy')}
                            </span>
                            {dataPoint === latestDataPoint && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Latest
                              </span>
                            )}
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(dataPoint.type)}`}>
                            {dataPoint.type}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Revenue</p>
                            <p className="text-lg font-bold text-green-600">{formatCurrency(dataPoint.revenue)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Gross Margin</p>
                            <p className="text-lg font-bold text-blue-600">{formatCurrency(dataPoint.grossMargin)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">GM Percentage</p>
                            <p className="text-lg font-bold text-purple-600">
                              {dataPoint.revenue > 0 ? ((dataPoint.grossMargin / dataPoint.revenue) * 100).toFixed(1) : 0}%
                            </p>
                          </div>
                        </div>

                        {dataPoint.description && (
                          <p className="text-sm text-gray-600 mt-2">{dataPoint.description}</p>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEditDataPoint(dataPoint)}
                          disabled={loading}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteDataPoint(dataPoint.id)}
                          disabled={loading}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RevenueGMManagement;